import { SelectItem } from '@/types/ui';

/** Normalizes a filter value into the string key used in availability sets. */
export const valueToAvailabilityKey = (value: unknown): string =>
  typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);

const valueToKey = valueToAvailabilityKey;

/**
 * Builds the set of "available" values from the filtered options list.
 * A value is available when it returns data for the current combination of
 * other active filters (i.e. it is present in the dynamically-filtered result).
 *
 * For hierarchical (tree-select) paths (`A__B__C`) every ancestor prefix is
 * also treated as available so a partially-available parent node stays enabled.
 */
export function buildAvailableValueSet<T>(filteredOptions: SelectItem<T>[] | undefined): Set<string> {
  const available = new Set<string>();
  if (!filteredOptions) return available;

  for (const option of filteredOptions) {
    const value = option.value;
    available.add(valueToKey(value));
    if (typeof value === 'string' && value.includes('__')) {
      const segments = value.split('__');
      let prefix = '';
      for (const segment of segments) {
        prefix = prefix ? `${prefix}__${segment}` : segment;
        available.add(prefix);
      }
    }
  }

  return available;
}

/**
 * Cascading filter model: show the currently available options as normal,
 * selectable entries, and append only the values the user *already selected*
 * that are no longer available for the current filter combination. Those
 * orphaned selections are flagged `disabled` — they stay visible and selected
 * but are excluded from the query.
 *
 * Newly-unavailable options the user never picked are simply absent from
 * `availableOptions`, so they are not shown at all (unlike the previous
 * behaviour which greyed out the whole universe).
 *
 * Labels for orphaned values are resolved from `universeOptions` (fetched with
 * dynamic filters off). For hierarchical (tree) paths the missing ancestor
 * chain is added too so the tree can still nest the disabled leaf.
 */
const NO_ORPHANED_OPTIONS: SelectItem<never>[] = [];

/** Marks every currently available option as selectable. */
export function toSelectableOptions<T>(availableOptions: SelectItem<T>[] | undefined): SelectItem<T>[] {
  return (availableOptions ?? []).map(option => ({ ...option, disabled: false }));
}

/**
 * Resolves the selected values that are no longer available into disabled
 * options, adding the missing ancestor chain for hierarchical paths.
 *
 * Returns a shared empty array when every selection is still available, so
 * callers can keep their option list referentially stable while the user is
 * only selecting and deselecting available values.
 */
export function buildOrphanedSelectionOptions<T>(
  availableOptions: SelectItem<T>[] | undefined,
  universeOptions: SelectItem<T>[] | undefined,
  selectedValues: readonly T[] | undefined,
): SelectItem<T>[] {
  if (!selectedValues?.length) return NO_ORPHANED_OPTIONS;

  const presentKeys = new Set((availableOptions ?? []).map(option => valueToKey(option.value)));
  const orphanedOptions: SelectItem<T>[] = [];
  let universeByKey: Map<string, SelectItem<T>> | undefined;

  const appendDisabled = (key: string, fallback: SelectItem<T> | null) => {
    if (presentKeys.has(key)) return;
    presentKeys.add(key);
    universeByKey ??= new Map((universeOptions ?? []).map(option => [valueToKey(option.value), option]));
    const universeOption = universeByKey.get(key);
    if (universeOption) orphanedOptions.push({ ...universeOption, disabled: true });
    else if (fallback) orphanedOptions.push({ ...fallback, disabled: true });
  };

  for (const selectedValue of selectedValues) {
    const key = valueToKey(selectedValue);
    if (presentKeys.has(key)) continue;

    if (typeof selectedValue === 'string' && selectedValue.includes('__')) {
      const segments = selectedValue.split('__');
      let prefix = '';
      for (const segment of segments) {
        prefix = prefix ? `${prefix}__${segment}` : segment;
        appendDisabled(prefix, null);
      }
    } else {
      appendDisabled(key, { value: selectedValue, label: String(selectedValue) });
    }
  }

  return orphanedOptions.length ? orphanedOptions : NO_ORPHANED_OPTIONS;
}

export function buildCascadingOptions<T>(
  availableOptions: SelectItem<T>[] | undefined,
  universeOptions: SelectItem<T>[] | undefined,
  selectedValues: readonly T[] | undefined,
): SelectItem<T>[] {
  const selectableOptions = toSelectableOptions(availableOptions);
  const orphanedOptions = buildOrphanedSelectionOptions(availableOptions, universeOptions, selectedValues);
  return orphanedOptions.length ? [...selectableOptions, ...orphanedOptions] : selectableOptions;
}

export type FilterActiveTotalCount = {
  active: number;
  total: number;
};

/**
 * Splits a filter's selected values into active (still available under the
 * current combination) and total, for the `{active}/{total}` input badge.
 * Hierarchical values match on their exact path.
 */
export function getActiveTotalCount(
  selectedValues: readonly unknown[] | undefined,
  availableValues: Set<string>,
): FilterActiveTotalCount {
  if (!selectedValues?.length) return { active: 0, total: 0 };
  const total = selectedValues.length;
  const active = selectedValues.reduce<number>(
    (count, selectedValue) => (availableValues.has(valueToKey(selectedValue)) ? count + 1 : count),
    0,
  );
  return { active, total };
}

/** Keeps only the selected values that are still available under the current combination. */
export function getActiveSelectedValues<T>(
  selectedValues: readonly T[] | undefined,
  availableValues: Set<string>,
): T[] {
  if (!selectedValues?.length) return [];
  return selectedValues.filter(selectedValue => availableValues.has(valueToKey(selectedValue)));
}

/** Formats the input badge label: simple count when all active, else `{active}/{total}`. */
export function formatActiveTotalBadge({ active, total }: FilterActiveTotalCount): string | null {
  if (total <= 0) return null;
  if (active === total) return String(total);
  return `${active}/${total}`;
}
